"use client";
import { Dialog } from "@base-ui/react";
import { X } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { cn } from "@/lib/utils";
import type { Question, QuestionPayload, QuizResult } from "@/types/content";
import { Button } from "../ui/button";
import ExtendedImage from "../ui/extended-image";
import { completeQuiz } from "./actions";
import ImageQuestion from "./quiz/image";
import MultiSelectQuestion from "./quiz/multi-select";
import TrueFalseQuestion from "./quiz/true-false";

const CountBar = ({
  questions,
  activeQuestion,
}: {
  questions: Question[];
  activeQuestion: number;
}) => {
  return (
    <div className="flex flex-row gap-0.5 justify-center items-center px-2 flex-1">
      {questions.map((question, index) => (
        <div
          className={cn("h-2 w-full bg-gray-300 rounded-full", {
            "bg-brand-blue-400": activeQuestion === index,
            "bg-brand-blue-600": index < activeQuestion,
          })}
          key={question.id}
        />
      ))}
    </div>
  );
};

const QuestionBuilder = ({ question }: { question: Question }) => {
  switch (question.type) {
    case "single-choice":
      return <MultiSelectQuestion question={question} />;
    case "true-false":
      return <TrueFalseQuestion question={question} />;
    case "image":
      return <ImageQuestion question={question} />;
    default:
      return question.type;
  }
};

const QuizDialog = ({
  contentId,
  payload,
}: {
  contentId: string;
  payload: QuestionPayload;
}) => {
  const [activeQuestion, setActiveQuestion] = useState<number>(0);
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const form = useForm<{ [key: string]: string }>();

  const isOpen = searchParams.has("solve");

  const handleOpenChange = (open: boolean) => {
    const params = new URLSearchParams(searchParams.toString());
    if (open) {
      setActiveQuestion(0);
      setQuizResult(null);
      form.reset();
      params.set("solve", "true");
    } else {
      params.delete("solve");
    }
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };
  const nextQuestion = () => {
    setActiveQuestion((prev) => {
      if (prev === payload.questions.length - 1) {
        return prev;
      }
      return prev + 1;
    });
  };

  const calculateScore = () => {
    const values = form.getValues();
    const score: Record<string, number> = {};
    Object.entries(values).forEach(([key, value]) => {
      const question = payload.questions.find((q) => q.id === key);
      if (!question) {
        return;
      }
      const { weight, options } = question;
      const opt = options.find((o) => o.id === value);
      if (!opt) {
        return;
      }
      opt.resultIds.forEach((id: string) => {
        score[id] = (score[id] || 0) + weight;
      });
    });

    let maxScore = -1;
    let maxResultId = "";
    Object.entries(score).forEach(([id, currentScore]) => {
      if (currentScore > maxScore) {
        maxScore = currentScore;
        maxResultId = id;
      }
    });

    const finalResult = payload.results?.find((r) => r.id === maxResultId);
    if (finalResult) {
      setQuizResult(finalResult);
      completeQuiz(contentId, {
        resultDescription: finalResult.description,
        resultId: finalResult.id,
        resultImageUrl: finalResult.image,
        resultTitle: finalResult.title,
      });
    }
  };

  return (
    <Dialog.Root
      disablePointerDismissal={true}
      onOpenChange={handleOpenChange}
      open={isOpen}
    >
      <Dialog.Trigger className="text-white bg-brand-blue-600 rounded-full px-6 py-2.5 text-base leading-5 font-semibold border-0 w-fit cursor-pointer">
        Quiz'i Çöz
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 min-h-dvh bg-white  transition-all duration-150 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0 dark:opacity-70 supports-[-webkit-touch-callout:none]:absolute" />
        <Dialog.Popup className="fixed top-0 left-0 w-full max-w-[100vw] h-screen max-h-screen bg-gray-100 text-gray-900 outline-1 outline-gray-200 transition-all duration-150 data-[ending-style]:scale-90 data-[ending-style]:opacity-0 data-[starting-style]:scale-90 data-[starting-style]:opacity-0 dark:outline-gray-300">
          <div className="flex flex-col justify-between h-full gap-4 pt-2">
            <div className="w-full md:w-2xl px-2 mx-auto flex flex-row justify-between">
              {!quizResult && (
                <>
                  <Dialog.Close className="hover:cursor-pointer">
                    <X />
                  </Dialog.Close>
                  <CountBar
                    activeQuestion={activeQuestion}
                    questions={payload.questions}
                  />
                  <div className="text-[10px] leading-3.5 font-bold text-gray-500">
                    <span className="text-sm leading-5 font-bold text-foreground">
                      {activeQuestion + 1}
                    </span>{" "}
                    / {payload.questions.length}
                  </div>
                </>
              )}
            </div>
            <div className="w-full md:w-2xl rounded-2xl mx-auto bg-white h-full p-6">
              {quizResult ? (
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <ExtendedImage
                    alt={`${quizResult.title} image`}
                    className="rounded-full size-44 border-2 border-brand-blue-600"
                    height={176}
                    src={quizResult.image as string}
                    width={176}
                  />
                  <h2 className="text-2xl font-bold text-brand-blue-600 capitalize">
                    {quizResult.title}
                  </h2>
                  <p className="text-lg font-medium text-gray-700">
                    {quizResult.description}
                  </p>
                </div>
              ) : (
                <FormProvider {...form}>
                  {payload.questions.map((question, index) => {
                    if (index !== activeQuestion) {
                      return null;
                    }
                    return (
                      <QuestionBuilder key={question.id} question={question} />
                    );
                  })}
                </FormProvider>
              )}
            </div>
            <div className="w-full py-4 bg-white border-t border-gray-300">
              <div className="w-full px-2 md:w-2xl mx-auto flex justify-end">
                {quizResult ? (
                  <Button
                    className="px-12"
                    onClick={() => handleOpenChange(false)}
                  >
                    Quiz'i Kapat
                  </Button>
                ) : (
                  <Button
                    className="px-12"
                    onClick={
                      activeQuestion + 1 !== payload.questions.length
                        ? nextQuestion
                        : calculateScore
                    }
                  >
                    {activeQuestion + 1 === payload.questions.length
                      ? "Sonucu Göster"
                      : "Devam"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default QuizDialog;
